import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerService } from 'ngx-spinner';
import { CreateBorrowerAccountMortgageRequest } from '../../models/create-borrower-account-mortgage-request.model';
import { CreateApplicationResponse } from '../../models/create-application-response.model';
import { ResetPasswordResult } from '../../models/auth/reset-password-result.model';
import { MortgageApplicationService } from '../../services/mortgage-application.service';
import { AuthService } from '../../services/auth.service';
import { AuthenticationRequest } from '../../models/auth/auth-request.model';
import { LoginStatus } from '../../models/auth/login-status.enum';
import { NavigationService } from '../../services/navigation.service';
import { ResetPasswordRequest } from '../../models/auth/reset-password-request.model';
import { AuthenticationUserCompany } from '../../models/auth/auth-response.model';
import { EnvironmentService } from 'projects/shared/services/environment.service';
import { TranslocoService } from '@jsverse/transloco';
import { Utils } from '../../services/utils';
import { PasswordPolicyResponse } from '../../models/auth/password-policy-response.model';

declare const Swal: any;

@Component({
    selector: 'reset-password-dialog',
    templateUrl: 'reset-password-dialog.component.html',
    styleUrls: ['reset-password-dialog.component.scss'],
    standalone: false
})
export class ResetPasswordDialogComponent implements OnInit {

  @ViewChild('resetPasswordForm') resetPasswordForm: NgForm | undefined;

  @Input()
  userName: string | undefined;

  @Input()
  createApplicationRequest: CreateBorrowerAccountMortgageRequest | undefined;

  @Input()
  applyingForANewLoan: boolean = false;

  securityCode: string | undefined;

  password: string = "";

  confirmedPassword: string = "";

  errorMessage: string | undefined;

  spinnerVisible: boolean = false;

  resetPasswordButtonText: string;

  resendCodeButtonText: string;

  result: ResetPasswordResult = new ResetPasswordResult();

  passwordErrors: string[] = null;

  userCompanyGuid: string = ""

  userScope: string;

  availableCompanies: AuthenticationUserCompany[] = [];

  passwordInputState = {
    id: 'password3',
    displayed: false,
  };
  confirmPasswordInputState = {
    id: 'confirmedPassword3',
    displayed: false,
  };

  protected passwordIsMinimum8Characters: boolean = false;
  protected passwordContainsDigit: boolean = false;
  protected passwordContainsUppercase: boolean = false;
  protected passwordContainsLowercase: boolean = false;
  protected passwordContainsSpecialCharacter: boolean = false;

  passwordPolicy: PasswordPolicyResponse;

  constructor(
    public activeModal: NgbActiveModal,
    private readonly _mortgageApplicationService: MortgageApplicationService,
    private readonly _authService: AuthService,
    private readonly _navigationService: NavigationService,
    private readonly _spinner: NgxSpinnerService,
    private readonly _environment: EnvironmentService,
    private readonly _translocoService: TranslocoService,
  ) {
    this.resetPasswordButtonText = this._translocoService.translate('auth.resetPasswordDialog.title');
    this.resendCodeButtonText = this._translocoService.translate('auth.resetPasswordDialog.resendCode');
  }

  ngOnInit() {
    this.userScope = this._environment.scope
    this.getAvailableCompanies();
  }

  onResendCodeClicked = () => {
    this.toggleProgress(true);
    this._authService.sendTwoFactorEmailCode(this.userCompanyGuid).subscribe(result => {
      Swal.fire(
        this._translocoService.translate('common.success'),
        this._translocoService.translate('auth.resetPasswordDialog.resendCodeSuccess', { email: this.userName }),
        'success'
      ).then((modalResult: any) => {
        if (modalResult.value) {
          // Nothing to do; the success dialog is purely informational.
        }
      });
    }, error => {
      this.errorMessage = error;
    }).add(() => {
      this.toggleProgress(false);
    });
  }

  onPasswordChanged = () => {
    const minLength = this.passwordPolicy?.minimumPasswordLength || 8;
    this.passwordContainsDigit = Utils.containsDigit(this.password);
    this.passwordIsMinimum8Characters = this.password.trim().length >= minLength;
    this.passwordContainsUppercase = Utils.containsUppercase(this.password);
    this.passwordContainsLowercase = Utils.containsLowercase(this.password);
    this.passwordContainsSpecialCharacter = Utils.containsSpecialCharacter(
      this.password
    );
  };

  get isPasswordValid(): boolean {
    if (!this.password) return false;
    if (!this.passwordIsMinimum8Characters) return false;
    const p = this.passwordPolicy;
    if (!p) {
      return this.passwordContainsDigit && this.passwordContainsUppercase
        && this.passwordContainsLowercase && this.passwordContainsSpecialCharacter;
    }
    if (p.requireDigit && !this.passwordContainsDigit) return false;
    if (p.requireUppercase && !this.passwordContainsUppercase) return false;
    if (p.requireLowercase && !this.passwordContainsLowercase) return false;
    if (p.requireSpecialCharacter && !this.passwordContainsSpecialCharacter) return false;
    return true;
  }

  onCompanyChanged(): void {
    if (this.userCompanyGuid) {
      this.loadPasswordPolicy(this.userCompanyGuid);
    }
  }


  onResetPasswordClicked = () => {
    this.resetPasswordForm?.form.markAllAsTouched();
    if (this.resetPasswordForm?.form.valid && this.isPasswordValid && this.createApplicationRequest) {
      this.createApplicationRequest.password = this.password;
      this.createApplicationRequest.resetPasswordCode = this.securityCode;
      this.toggleProgress(true);
      if (this.applyingForANewLoan) {
        this.applyForANewLoan();
      } else {
        this.resetPasswordAndGotoBorrowerPortal();
      }
    }
  }

  protected onPasswordStateClicked = (state: { id: string, displayed: boolean }) => {
    state.displayed = !state.displayed;
    const passwordElement = document.getElementById(state.id);
    passwordElement.setAttribute('type', state.displayed ? 'text' : 'password');
  }

  private resetPasswordAndGotoBorrowerPortal = () => {
    this.passwordErrors = null;
    const resetPasswordRequest: ResetPasswordRequest = {
      email: this.userName,
      password: this.password,
      confirmPassword: this.confirmedPassword,
      code: this.securityCode,
      userCompanyGuid: this.userCompanyGuid,
    }
    this.toggleProgress(true);
    this._authService.resetPasswordWithRecurityCode(resetPasswordRequest).subscribe(response => {
      this.toggleProgress(false);
      this.loginAndGotoBorrowerPortal();
    }, error => {
      this.toggleProgress(false);
      this.passwordErrors = this.formatErrorMessageForPasswordReset(error);
    });
  }

  private loginAndGotoBorrowerPortal = () => {
    const loginRequest: AuthenticationRequest = {
      companyGuid: this.createApplicationRequest.companyGuid,
      userCompanyGuid: this.userCompanyGuid,
      username: this.userName,
      password: this.password,
      rememberMe: false,
      scope: 'Borrower'
    }
    this.toggleProgress(true);
    this._authService.signIn(loginRequest).subscribe(response => {
      this.toggleProgress(false);
      if (response.loginStatus === LoginStatus.Error) {
        this.errorMessage = response.errorMessage;
        this.createApplicationRequest.password = null;
        return;
      }
      this.activeModal.close();
      this._navigationService.navigateToPath("borrower-portal", true);
    }, error => {
      this.toggleProgress(false);
    });
  }

  private applyForANewLoan = () => {
    this._mortgageApplicationService.saveLoanApplication(this.createApplicationRequest).subscribe((response: CreateApplicationResponse) => {
      if (response.authToken) {
        this.result.authToken = response.authToken;
        this.result.refreshToken = response.refreshToken;
        this.result.appId = response.applicationId;
        this.result.newPassword = this.password;
        this.activeModal.close(this.result);
      } else {
        if (response.loginError) {
          this.errorMessage = response.loginError;
          return;
        }
        this.errorMessage = this._translocoService.translate('auth.existingAccount.incorrectCredentials');
      }
    }, error => {
      this.errorMessage = this._translocoService.translate('auth.existingAccount.unableToLogin');
    }).add(() => {
      this.toggleProgress(false);
    });
  }

  private toggleProgress = (value: boolean) => {
    if (value) {
      this._spinner.show();
    } else {
      this._spinner.hide();
    }
    this.spinnerVisible = value;
    if (value) {
      this.resetPasswordButtonText = this._translocoService.translate('common.pleaseWait');
    } else {
      this.resetPasswordButtonText = this._translocoService.translate('auth.resetPasswordDialog.title');
    }
  }

  private formatErrorMessageForPasswordReset = (error: any): string[] => {
    const passwordErrors = error?.error?.errors?.password;
    if (passwordErrors && passwordErrors.length) {
      return passwordErrors;
    }
    const errorMessage = error?.error || this._translocoService.translate('auth.resetPasswordDialog.errorResettingPassword');
    return [errorMessage];
  }

  private loadPasswordPolicy(userCompanyGuid: string): void {
    this._authService.getPasswordPolicy(userCompanyGuid).subscribe({
      next: (policy) => {
        this.passwordPolicy = policy;
        this.onPasswordChanged();
      }
    });
  }

  private getAvailableCompanies = () => {
    this._spinner.show();
    this._authService.getAvailableCompanies(this.userName, this.userScope || 'AgentAndBorrower').subscribe({
      next: (response) => {
        this.availableCompanies = response || [];

        if (this.availableCompanies.length === 1) {
          this.userCompanyGuid = this.availableCompanies[0].userCompanyGuid;
          this.loadPasswordPolicy(this.userCompanyGuid);
        }
      },
      error: (error) => {
        this.errorMessage = error?.error?.detail || error?.error?.message || this._translocoService.translate('auth.sendCode.unableToGetCompanies');
      }
    }).add(() => {
      this._spinner.hide();
    });
  }
}
